import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Typography, Divider, CircularProgress } from '@mui/material';
import { heritageColors } from '../../../../heritageTheme.ts';
import { getIconById } from '../../../NitzavSpotlightWidget/iconOptions';
import { PARNAS_TYPE_LABELS, toHebrewYear, fetchHebDateLabel } from '../../parnasConstants';

/**
 * WeeklyYear — כל פרשיות השנה העברית בגריד שמתמלא ב-view
 * ללא גלילה — הרכיב יתרחב לפי גודל הווידג'ט.
 * 7 עמודות × N שורות, פרשה נוכחית מודגשת בזהב.
 */

const GOLD = '#b8860b';
const COLS = 7;

// ─── Component ────────────────────────────────────────────────────────────────

const WeeklyYear = ({
  parnasType                  = 'maor',
  parnasTypeCustomLabel       = '',
  parnasTypeIcon              = 'star',
  currentParshaName           = '',
  currentParshaShabbatDate    = '',
  todayHYear                  = null,
  todayHebrewLabel            = '',
  title                       = '',
  showTitle                   = false,
  sponsors                    = [],
  effectiveDateISO            = '',
  weeklyYearDisplayYear       = null,   // null = שנה נוכחית (auto)
}) => {
  const TypeIcon  = getIconById(parnasTypeIcon || 'star');
  const typeLabel = parnasType === 'acher' && parnasTypeCustomLabel
    ? parnasTypeCustomLabel
    : (PARNAS_TYPE_LABELS[parnasType] || '');

  const [parshiyot,    setParshiyot]    = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [resolvedYear, setResolvedYear] = useState(todayHYear);
  const [hebDates,     setHebDates]     = useState({});   // { iso: "כ"ז אדר" }
  const [cellFontSize, setCellFontSize] = useState(null); // px — מחושב לפי גודל תא הגריד

  const gridRef        = useRef(null);
  const resizeTimerRef = useRef(null);

  const weeklySponsorByDate = useMemo(() => {
    const map = {};
    for (const sponsor of sponsors)
      for (const slot of (sponsor.slots || []))
        if (slot.type === 'weekly' && slot.parshaShabbatDate)
          map[slot.parshaShabbatDate] = { sponsorName: sponsor.name, note: slot.note || '' };
    return map;
  }, [sponsors]);

  // ── Resolve which Hebrew year to display ──────────────────────────────────
  useEffect(() => {
    if (weeklyYearDisplayYear) { setResolvedYear(weeklyYearDisplayYear); return; }
    if (todayHYear)            { setResolvedYear(todayHYear);            return; }
    if (!effectiveDateISO) return;
    fetch(`https://www.hebcal.com/converter?cfg=json&date=${effectiveDateISO}&g2h=1`)
      .then(r => r.json())
      .then(d => setResolvedYear(d.hy || null))
      .catch(() => {});
  }, [weeklyYearDisplayYear, todayHYear, effectiveDateISO]);

  // ── Fetch parshiyot for Hebrew year ───────────────────────────────────────
  useEffect(() => {
    if (!resolvedYear) return;
    let cancelled = false;
    setFetchLoading(true);

    fetch(`https://www.hebcal.com/hebcal?v=1&cfg=json&year=${resolvedYear}&yt=H&s=on&maj=on&c=off`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;

        // holiday labels per Shabbat date (for weeks without a regular parsha name)
        const holidayByDate = {};
        (data.items || [])
          .filter(i => i.category === 'holiday' && i.subcat !== 'minor')
          .forEach(i => { if (!holidayByDate[i.date]) holidayByDate[i.date] = i.hebrew || i.title; });

        const items = (data.items || [])
          .filter(i => i.category === 'parashat')
          .sort((a, b) => (a.date < b.date ? -1 : 1))
          .map(i => ({ ...i, holidayLabel: holidayByDate[i.date] || null }));

        setParshiyot(items);
        setFetchLoading(false);
      })
      .catch(() => { if (!cancelled) setFetchLoading(false); });

    return () => { cancelled = true; };
  }, [resolvedYear]);

  // ── Fetch Hebrew dates for all parsha Shabbat dates ──────────────────────
  useEffect(() => {
    if (!parshiyot.length) return;
    let cancelled = false;

    Promise.all(
      parshiyot.map(({ date }) =>
        fetchHebDateLabel(date).then(label => ({ date, label }))
      )
    ).then(results => {
      if (cancelled) return;
      const map = {};
      results.forEach(({ date, label }) => { map[date] = label; });
      setHebDates(map);
    });

    return () => { cancelled = true; };
  }, [parshiyot]);

  // ── פונטים פרופורציונליים לגודל תא הגריד ────────────────────────────────
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const rows = Math.ceil(parshiyot.length / COLS) || 8;
    const obs = new ResizeObserver(([entry]) => {
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = setTimeout(() => {
        const { width, height } = entry.contentRect;
        const cellH = height / rows;
        const cellW = width / COLS;
        setCellFontSize(Math.max(9, Math.round(Math.min(cellH, cellW * 1.4) * 0.22)));
      }, 60);
    });
    obs.observe(el);
    return () => {
      obs.disconnect();
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
    };
  }, [parshiyot.length]);

  const rows = Math.ceil(parshiyot.length / COLS) || 8;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box dir="rtl" sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <Box sx={{
        px: 2, pt: 1.5, pb: 1,
        display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0,
      }}>
        <TypeIcon sx={{ fontSize: 40, color: heritageColors.gold }} />
        <Typography variant="h3" sx={{ color: heritageColors.navy, fontWeight: 700 }}>
          {showTitle && title ? title : `פרנסי ${typeLabel}`}
        </Typography>
        {resolvedYear && (
          <Typography variant="body2" sx={{
            color: heritageColors.slate, fontSize: '0.75em', opacity: 0.7, mr: 'auto',
          }}>
            {toHebrewYear(resolvedYear)}
          </Typography>
        )}
      </Box>

      <Divider sx={{ borderColor: heritageColors.border, flexShrink: 0 }} />

      {/* Grid — fills all remaining space, no scroll */}
      <Box ref={gridRef} sx={{
        flex: 1,
        overflow: 'hidden',
        p: 1,
        fontSize: cellFontSize ? `${cellFontSize}px` : undefined,
        display: fetchLoading ? 'flex' : 'grid',
        ...(fetchLoading
          ? { alignItems: 'center', justifyContent: 'center' }
          : {
              gridTemplateColumns: `repeat(${COLS}, 1fr)`,
              gridTemplateRows:    `repeat(${rows}, 1fr)`,
              gap: '5px',
            }
        ),
      }}>
        {fetchLoading ? (
          <CircularProgress size={24} sx={{ color: heritageColors.gold }} />
        ) : parshiyot.map(item => {
          const isCurrent  = item.date === currentParshaShabbatDate;
          const isPast     = !isCurrent && item.date < currentParshaShabbatDate;
          const isFuture   = !isCurrent && !isPast;
          const sponsor    = weeklySponsorByDate[item.date] || null;
          const parshaName = item.hebrew || item.title || '';
          const dateStr    = hebDates[item.date] || '…';

          return (
            <Box
              key={item.date}
              sx={{
                display:        'flex',
                flexDirection:  'column',
                alignItems:     'center',
                justifyContent: 'flex-start',
                textAlign:      'center',
                overflow:       'hidden',
                position:       'relative',
                borderRadius:   '5px',
                pt:             isCurrent ? '3px' : '5px',
                pb:             '4px',
                px:             '4px',
                gap:            '2px',

                // ── border: current=gold thick, sponsored=warm, vacant=light gray ──
                border: isCurrent
                  ? `2.5px solid ${GOLD}`
                  : sponsor
                  ? `1.5px solid #d4b896`
                  : `1px solid ${heritageColors.borderLight}`,

                // ── background: sponsored=warm cream, vacant=cool white ──
                background: isCurrent
                  ? 'linear-gradient(160deg, #fffcf2 0%, #fef5d0 100%)'
                  : sponsor
                  ? 'linear-gradient(160deg, #fffef9 0%, #fdf8ec 100%)'
                  : '#f9f9f8',

                boxShadow: isCurrent
                  ? '0 4px 18px rgba(184,134,11,0.30), 0 1px 4px rgba(0,0,0,0.08)'
                  : sponsor
                  ? '0 1px 5px rgba(180,140,60,0.12)'
                  : 'none',
              }}
            >
              {/* Top gold accent stripe — current only */}
              {isCurrent && (
                <Box sx={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
                  background: `linear-gradient(to left, transparent, ${GOLD} 25%, #d4a017 50%, ${GOLD} 75%, transparent)`,
                }} />
              )}

              {/* Parsha name */}
              <Typography sx={{
                color:        heritageColors.navy,
                fontWeight:   isCurrent ? 800 : 600,
                fontSize:     isCurrent ? '0.68em' : '0.6em',
                lineHeight:   1.2,
                overflow:     'hidden',
                textOverflow: 'ellipsis',
                whiteSpace:   'nowrap',
                maxWidth:     '100%',
              }}>
                {parshaName}
              </Typography>

              {/* Holiday sub-label */}
              {item.holidayLabel && (
                <Typography sx={{
                  color:        heritageColors.slate,
                  fontSize:     '0.5em',
                  opacity:      0.65,
                  lineHeight:   1,
                  overflow:     'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace:   'nowrap',
                  maxWidth:     '100%',
                }}>
                  {item.holidayLabel}
                </Typography>
              )}

              {/* Date */}
              <Typography sx={{
                color:      heritageColors.slate,
                fontSize:   '0.52em',
                opacity:    isCurrent ? 0.8 : 0.55,
                lineHeight: 1,
              }}>
                {dateStr}
              </Typography>

              {/* Thin divider */}
              <Box sx={{
                width:      isCurrent ? '60%' : '45%',
                height:     '1px',
                bgcolor:    isCurrent ? GOLD : sponsor ? '#d4b896' : heritageColors.borderLight,
                opacity:    isCurrent ? 0.6 : 0.5,
                my:         '1px',
                flexShrink: 0,
              }} />

              {/* Sponsor or status */}
              {sponsor ? (
                <>
                  <Typography sx={{
                    color:        heritageColors.navy,
                    fontWeight:   isCurrent ? 800 : 700,
                    fontSize:     isCurrent ? '0.64em' : '0.58em',
                    lineHeight:   1.2,
                    overflow:     'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace:   'nowrap',
                    maxWidth:     '100%',
                  }}>
                    {sponsor.sponsorName}
                  </Typography>
                  {sponsor.note ? (
                    <Typography sx={{
                      color:                heritageColors.slate,
                      fontSize:             '0.48em',
                      lineHeight:           1.15,
                      maxWidth:             '100%',
                      overflow:             'hidden',
                      display:              '-webkit-box',
                      WebkitLineClamp:      2,
                      WebkitBoxOrient:      'vertical',
                      textOverflow:         'ellipsis',
                      opacity:              0.75,
                    }}>
                      {sponsor.note}
                    </Typography>
                  ) : null}
                </>
              ) : isFuture || isCurrent ? (
                <>
                  <Typography sx={{
                    color:      heritageColors.slate,
                    fontWeight: 500,
                    fontSize:   '0.5em',
                    opacity:    0.5,
                    lineHeight: 1.1,
                  }}>
                    פנוי
                  </Typography>
                  <Typography sx={{
                    color:    heritageColors.slate,
                    fontSize: '0.44em',
                    opacity:  0.4,
                    lineHeight: 1,
                  }}>
                    לזכות לתרום
                  </Typography>
                </>
              ) : null}
            </Box>
          );
        })}
      </Box>

      {/* Footer */}
      {(currentParshaName || todayHebrewLabel) && (
        <>
          <Divider sx={{ borderColor: heritageColors.border, flexShrink: 0 }} />
          <Box sx={{
            px: 2, py: 0.6, flexShrink: 0,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            {currentParshaName ? (
              <Typography variant="body2" sx={{ color: heritageColors.slate, fontSize: '0.8em' }}>
                פרשת {currentParshaName}
              </Typography>
            ) : <Box />}
            {todayHebrewLabel && (
              <Typography variant="body2" sx={{ color: heritageColors.slate, fontSize: '0.8em' }}>
                {todayHebrewLabel}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default WeeklyYear;
